import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play, Layers, Activity, Database } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, Cell, LineChart, Line,
} from 'recharts'

const TABS = ['Universe', 'Strategy stats', 'Correlation & clusters', 'Screening', 'Candidates', 'Portfolio', 'Walk-forward', 'Tail risk', 'Regime Lab', 'Event study'] as const
// The screening engine emits 116 columns; show the ones that drive the decision.
// HAC-adjusted t-stat/p-value rather than the classic ones — monthly hedge-fund
// returns are autocorrelated enough that the naive versions overstate significance.
const SCREEN_COLS = [
  'ticker', 'asset_class', 'risk_premia', 'provider',
  'corr_vs_portfolio (m)', 'beta_vs_portfolio (m)', 'alpha_ann (m)',
  'alpha_pvalue_hac (m)', 'ir_tstats_hac (m)', 'dd_corr_max_pairwise (m)', 'n_months_prod',
]
const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
// The ticker stays available — it is what the engine keys on — but it is an
// identifier, not a label, so it reads as secondary to the strategy's name.
const tickerTd = 'px-3 py-2 font-mono text-xs text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-800 whitespace-nowrap'
const inp = 'rounded-md border border-gray-300 px-2 py-1 text-sm'
const num = (v: any, d = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(d))

export default function QISConstruction() {
  const [tab, setTab] = useState<string>('Universe')
  // shared filters
  const [assetClass, setAssetClass] = useState('')
  const [provider, setProvider] = useState('')
  const [riskPremia, setRiskPremia] = useState('')
  // stats params
  const [isDate, setIsDate] = useState('2010-01-01')
  const [osDate, setOsDate] = useState('2020-01-01')
  const [limit, setLimit] = useState(150)
  const [statsRun, setStatsRun] = useState(0)
  // correlation params
  const [shrinkK, setShrinkK] = useState(1.0)
  const [deflated, setDeflated] = useState(true)
  const [dupOnly, setDupOnly] = useState(true)
  const [corrThr, setCorrThr] = useState(0.9)
  const [corrLimit, setCorrLimit] = useState(120)
  const [corrRun, setCorrRun] = useState(0)
  // screening params
  const [screenLimit, setScreenLimit] = useState(150)
  const [screenOverlap, setScreenOverlap] = useState(24)
  const [screenRefFund, setScreenRefFund] = useState('')
  const [screenRun, setScreenRun] = useState(0)

  const filterParams = {
    asset_classes: assetClass || undefined,
    providers: provider || undefined,
    risk_premias: riskPremia || undefined,
  }

  const uniQ = useQuery({
    queryKey: ['qis-universe', assetClass, provider, riskPremia],
    queryFn: () => api.get('/qis/universe', { params: filterParams }).then(r => r.data),
  })

  const statsQ = useQuery({
    queryKey: ['qis-stats', statsRun],
    queryFn: () => api.get('/qis/stats', {
      params: { ...filterParams, is_date: isDate, os_date: osDate, limit_tickers: limit },
    }).then(r => r.data),
    enabled: statsRun > 0,
  })

  const corrQ = useQuery({
    queryKey: ['qis-corr', corrRun],
    queryFn: () => api.get('/qis/correlation', {
      params: { ...filterParams, shrinkage_k: shrinkK, deflated, corr_threshold: corrThr, limit_tickers: corrLimit },
    }).then(r => r.data),
    enabled: corrRun > 0,
  })

  const screenQ = useQuery({
    queryKey: ['qis-screening', screenRun],
    queryFn: () => api.get('/qis/screening', {
      params: {
        ...filterParams, limit_tickers: screenLimit, min_overlap_months: screenOverlap,
        reference_fund_id: screenRefFund || undefined,
      },
    }).then(r => r.data),
    enabled: screenRun > 0,
  })

  const refBookQ = useQuery({
    queryKey: ['qis-reference-book'],
    queryFn: () => api.get('/qis/reference-book').then(r => r.data),
  })

  const refFundsQ = useQuery({
    queryKey: ['qis-reference-funds'],
    queryFn: () => api.get('/qis/reference-funds').then(r => r.data),
  })

  // ---- construction stage ----
  const [candK, setCandK] = useState(5)
  const [candLimit, setCandLimit] = useState(250)
  const [candRun, setCandRun] = useState(0)
  const [maxConst, setMaxConst] = useState(10)
  const [minCorr, setMinCorr] = useState(0.3)
  const [simRun, setSimRun] = useState(0)
  const [rollWindow, setRollWindow] = useState(5)
  const [rollFreq, setRollFreq] = useState(2)
  const [rollRun, setRollRun] = useState(0)
  const [nLegs, setNLegs] = useState(2)
  const [trRun, setTrRun] = useState(0)
  const [bwPeriod, setBwPeriod] = useState(21)
  const [nBins, setNBins] = useState(10)
  const [groupBy, setGroupBy] = useState('')
  const [rgRun, setRgRun] = useState(0)
  const [evStart, setEvStart] = useState('2020-02-19')
  const [evEnd, setEvEnd] = useState('2020-03-23')
  const [evRun, setEvRun] = useState(0)

  const candQ = useQuery({
    queryKey: ['qis-cand', candRun],
    queryFn: () => api.get('/qis/candidates', {
      params: { ...filterParams, shrinkage_k: candK, limit_tickers: candLimit },
    }).then(r => r.data),
    enabled: candRun > 0,
  })
  const simQ = useQuery({
    queryKey: ['qis-sim', simRun],
    queryFn: () => api.get('/qis/simulate', {
      params: { ...filterParams, shrinkage_k: candK, max_constituents: maxConst,
                min_corr_threshold: minCorr, limit_tickers: candLimit },
    }).then(r => r.data),
    enabled: simRun > 0,
  })
  const rollQ = useQuery({
    queryKey: ['qis-roll', rollRun],
    queryFn: () => api.get('/qis/rolling-simulation', {
      params: { ...filterParams, max_constituents: maxConst, shrinkage_k: candK,
                window_universe_selection_years: rollWindow,
                rolling_window_freq_years: rollFreq, limit_tickers: 400 },
    }).then(r => r.data),
    enabled: rollRun > 0,
  })

  const trQ = useQuery({
    queryKey: ['qis-tr', trRun],
    queryFn: () => api.get('/qis/tail-risk', {
      params: { ...filterParams, max_constituents: maxConst, n_legs: nLegs,
                shrinkage_k: candK, limit_tickers: candLimit },
    }).then(r => r.data),
    enabled: trRun > 0,
  })

  const rgQ = useQuery({
    queryKey: ['qis-regime', rgRun],
    queryFn: () => api.get('/qis/regime', {
      params: { ...filterParams, bw_period: bwPeriod, n_bins: nBins,
                group_by: groupBy || undefined, limit_tickers: candLimit },
    }).then(r => r.data),
    enabled: rgRun > 0,
  })

  const evQ = useQuery({
    queryKey: ['qis-event', evRun],
    queryFn: () => api.get('/qis/event-study', {
      params: { ...filterParams, event_start: evStart, event_end: evEnd,
                top_n: 25, limit_tickers: candLimit },
    }).then(r => r.data),
    enabled: evRun > 0,
  })

  const downloadReport = async () => {
    const r = await api.get('/qis/report.xlsx', {
      params: { ...filterParams, shrinkage_k: candK, max_constituents: maxConst, limit_tickers: candLimit },
      responseType: 'blob',
    })
    const url = URL.createObjectURL(r.data); const a = document.createElement('a')
    a.href = url; a.download = 'qis-report.xlsx'; a.click(); URL.revokeObjectURL(url)
  }

  const u = uniQ.data
  const breakdown = (obj: Record<string, number> | undefined, n = 8) =>
    Object.entries(obj || {}).slice(0, n).map(([name, value]) => ({ name, value }))

  return (
    <div className="space-y-4 p-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">QIS Construction</h1>
          <p className="text-sm text-gray-500">
            Quantitative Investment Strategies — universe screening, live-vs-backtest decay, correlation clustering.
            {u ? ` ${u.filtered} / ${u.total_universe} strategies` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input className={inp} placeholder="asset class" value={assetClass} onChange={e => setAssetClass(e.target.value)} />
          <input className={inp} placeholder="provider" value={provider} onChange={e => setProvider(e.target.value)} />
          <input className={inp} placeholder="risk premia" value={riskPremia} onChange={e => setRiskPremia(e.target.value)} />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 border-b border-gray-200 text-sm">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`-mb-px border-b-2 pb-2 pt-1 ${tab === t ? 'border-blue-600 font-medium text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{t}</button>
        ))}
      </div>

      {/* ------------------------------------------------ Universe */}
      {tab === 'Universe' && (
        <div className="space-y-4">
          {uniQ.isLoading && <Spinner />}
          {u && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Total universe" value={u.total_universe} icon={<Database size={15} />} />
                <Kpi label="After filters" value={u.filtered} icon={<Layers size={15} />} />
                <Kpi label="Providers" value={Object.keys(u.by_provider || {}).length} />
                <Kpi label="Asset classes" value={Object.keys(u.by_asset_class || {}).length} />
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Panel title="By asset class"><Breakdown data={breakdown(u.by_asset_class)} /></Panel>
                <Panel title="By provider"><Breakdown data={breakdown(u.by_provider)} /></Panel>
                <Panel title="By risk premia"><Breakdown data={breakdown(u.by_risk_premia)} /></Panel>
                <Panel title="By currency"><Breakdown data={breakdown(u.by_currency)} /></Panel>
              </div>
              <Panel title="Strategies (first 500)">
                <div className="max-h-96 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{['Strategy', 'Ticker', 'Provider', 'Asset class', 'Risk premia', 'Style', 'Currency', 'Live date'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {(u.rows || []).map((r: any) => (
                        <tr key={r.ticker} className="hover:bg-gray-50">
                          <td className={`${td} whitespace-normal font-medium`} title={r.description || r.strategy}>{r.strategy || <span className="text-gray-400">—</span>}</td>
                          <td className={tickerTd}>{r.ticker}</td>
                          <td className={td}>{r.provider}</td><td className={td}>{r.asset_class}</td>
                          <td className={td}>{r.risk_premia}</td><td className={td}>{r.style}</td>
                          <td className={td}>{r.currency}</td><td className={td}>{r.live_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------ Stats */}
      {tab === 'Strategy stats' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">IS start <input className={`${inp} ml-1`} type="date" value={isDate} onChange={e => setIsDate(e.target.value)} /></label>
            <label className="text-sm">OS start <input className={`${inp} ml-1`} type="date" value={osDate} onChange={e => setOsDate(e.target.value)} /></label>
            <label className="text-sm">Max tickers <input className={`${inp} ml-1 w-24`} type="number" value={limit} onChange={e => setLimit(Number(e.target.value))} /></label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
              onClick={() => setStatsRun(n => n + 1)}><Play size={15} />Run stats</button>
          </div>
          {statsQ.isFetching && <Spinner label="Computing per-strategy statistics…" />}
          {statsQ.data && (
            <>
              <div className="flex flex-wrap gap-3 text-sm">
                <Badge>{statsQ.data.n} strategies</Badge>
                <Badge>{statsQ.data.n_skipped} skipped (insufficient history)</Badge>
                <Badge>reference: {statsQ.data.reference?.mode}</Badge>
                <Badge>window {statsQ.data.window?.portfolio_start} → {statsQ.data.window?.portfolio_end}</Badge>
              </div>
              <Panel title="Live vs backtest decay — Sharpe (backtest) vs Sharpe (live)">
                <p className="mb-2 text-xs text-gray-500">Points below the diagonal decayed out of sample. Bubble size = full-history Sharpe.</p>
                <DecayScatter rows={statsQ.data.stats} />
              </Panel>
              <Panel title="Per-strategy statistics">
                <div className="max-h-[30rem] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{['Strategy', 'Ticker', 'SR full', 'SR backtest', 'SR live', 'SR decay', 'PSR', 'minTRL', 'Max DD', 'Skew'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {(statsQ.data.stats || []).map((r: any) => (
                        <tr key={r.ticker} className="hover:bg-gray-50">
                          <td className={`${td} whitespace-normal font-medium`} title={r.strategy}>{r.strategy || <span className="text-gray-400">—</span>}</td>
                          <td className={tickerTd}>{r.ticker}</td>
                          <td className={td}>{num(r.sharpe_ann_fh)}</td>
                          <td className={td}>{num(r.sharpe_ann_bk)}</td>
                          <td className={`${td} ${(r.sharpe_ann_live ?? 0) < 0 ? 'text-red-500' : ''}`}>{num(r.sharpe_ann_live)}</td>
                          <td className={`${td} ${(r.sr_decay ?? 0) < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{num(r.sr_decay)}</td>
                          <td className={td}>{num(r.psr_fh)}</td>
                          <td className={td}>{num(r.minTRL_fh, 0)}</td>
                          <td className={td}>{num(r.max_dd_fh, 3)}</td>
                          <td className={td}>{num(r.skew_fh)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
          {!statsQ.data && !statsQ.isFetching && <Empty text="Set a window and run the statistics stage." />}
        </div>
      )}

      {/* ------------------------------------------------ Correlation */}
      {tab === 'Correlation & clusters' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={deflated} onChange={e => setDeflated(e.target.checked)} />Deflated</label>
            <label className="text-sm">Shrinkage k <input className={`${inp} ml-1 w-20`} type="number" step="0.1" value={shrinkK} onChange={e => setShrinkK(Number(e.target.value))} /></label>
            <label className="text-sm">Cluster threshold <input className={`${inp} ml-1 w-20`} type="number" step="0.05" value={corrThr} onChange={e => setCorrThr(Number(e.target.value))} /></label>
            <label className="text-sm">Max tickers <input className={`${inp} ml-1 w-24`} type="number" value={corrLimit} onChange={e => setCorrLimit(Number(e.target.value))} /></label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
              onClick={() => setCorrRun(n => n + 1)}><Activity size={15} />Run correlation</button>
          </div>
          {corrQ.isFetching && <Spinner label="Computing correlation matrix and clusters…" />}
          {corrQ.data && !corrQ.data.error && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Tickers" value={corrQ.data.n_tickers} />
                <Kpi label="Clusters" value={corrQ.data.n_clusters} />
                <Kpi label="Avg correlation" value={num(corrQ.data.avg_corr, 4)} />
                <Kpi label="Max correlation" value={num(corrQ.data.max_corr, 4)} />
              </div>
              {(() => {
                const groups = groupClusters(corrQ.data.clusters || [])
                const families = groups.filter(g => g.rows.length > 1)
                const shown = dupOnly ? families : groups
                const nDup = families.reduce((a, g) => a + g.rows.length, 0)
                return (
                  <Panel title="Duplicate families (clusters)">
                    <p className="mb-2 text-xs text-gray-500">
                      Strategies whose pairwise correlation ≥ {corrThr} are grouped — candidates for
                      de-duplication before portfolio construction. Only a cluster with more than one
                      member is a duplicate family; a cluster of one is simply a strategy nothing else
                      resembles.
                    </p>
                    <div className="mb-2 flex flex-wrap items-center gap-3 text-xs">
                      <label className="flex items-center gap-1.5">
                        <input type="checkbox" checked={dupOnly} onChange={e => setDupOnly(e.target.checked)} />
                        Only duplicate families
                      </label>
                      <span className="text-gray-500">
                        {families.length} famil{families.length === 1 ? 'y' : 'ies'} covering {nDup} of{' '}
                        {(corrQ.data.clusters || []).length} strategies
                        {groups.length - families.length > 0 &&
                          ` · ${groups.length - families.length} unique`}
                      </span>
                    </div>
                    {shown.length === 0 && (
                      <Empty text={`No cluster has more than one member at a threshold of ${corrThr}. Lower it to find near-duplicates.`} />
                    )}
                    <div className="max-h-[32rem] overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="sticky top-0 bg-white">
                          <tr>{['Cluster', 'Strategy', 'Ticker', 'Provider', 'Factor', 'Asset class', 'Sharpe', 'Live days']
                            .map(h => <th key={h} className={th}>{h}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {shown.map(g => g.rows.map((r: any, j: number) => (
                            <tr key={`${g.id}-${j}`}
                                className={`hover:bg-gray-50 ${j === 0 && g.rows.length > 1 ? 'border-t-2 border-gray-300' : ''}`}>
                              <td className={td}>
                                {j === 0 && (
                                  <span className="inline-flex items-center gap-1">
                                    {g.id}
                                    {g.rows.length > 1 && (
                                      <span className="rounded-full bg-amber-100 px-1.5 text-[10px] font-medium text-amber-800">
                                        ×{g.rows.length}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </td>
                              <td className={`${td} whitespace-normal font-medium`} title={r.strategy}>
                                {r.strategy || <span className="text-gray-400">—</span>}
                              </td>
                              <td className={`${td} font-mono text-xs text-gray-500`}>{r.ticker}</td>
                              <td className={td}>{r.provider}</td>
                              <td className={td}>{r.factor ?? r.risk_premia}</td>
                              <td className={td}>{r.asset_class}</td>
                              <td className={td}>{num(r.sharpe_ann)}</td>
                              <td className={td}>{r.n_days_live ?? '—'}</td>
                            </tr>
                          )))}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                )
              })()}
            </>
          )}
          {corrQ.data?.error && <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">{corrQ.data.error}</div>}
          {!corrQ.data && !corrQ.isFetching && <Empty text="Configure deflation/shrinkage and run the correlation stage." />}
        </div>
      )}

      {/* ------------------------------------------------ Candidates */}
      {tab === 'Screening' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Strategies <input className={`${inp} ml-1 w-20`} type="number" value={screenLimit} onChange={e => setScreenLimit(Number(e.target.value))} /></label>
            <label className="text-sm">Min overlap (months) <input className={`${inp} ml-1 w-16`} type="number" value={screenOverlap} onChange={e => setScreenOverlap(Number(e.target.value))} /></label>
            <label className="text-sm">Reference book
              <select className={`${inp} ml-1`} value={screenRefFund} onChange={e => setScreenRefFund(e.target.value)}>
                <option value="">Live house book (default)</option>
                {(refFundsQ.data?.funds || []).map((f: any) => (
                  <option key={f.fund_id} value={f.fund_id}>{f.name} ({f.n_months}m)</option>
                ))}
              </select>
            </label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={() => setScreenRun(n => n + 1)}><Play size={15} />Run screening</button>
          </div>

          {refBookQ.data?.reference && screenRun === 0 && (
            <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
              Default benchmark: <b>{refBookQ.data.reference.name}</b>
              {refBookQ.data.reference.as_of && <> as of {refBookQ.data.reference.as_of}</>}
              {refBookQ.data.reference.n_months && <> — {refBookQ.data.reference.n_months} months, {refBookQ.data.reference.n_constituents} constituents</>}
            </div>
          )}

          {screenQ.isFetching && <Spinner label="Regressing every strategy against the book..." />}
          {screenQ.data && !screenQ.data.available && (
            <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">{screenQ.data.reason}</div>
          )}

          {screenQ.data?.available && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Strategies screened" value={screenQ.data.n_screened} />
                <Kpi label="Overlap months" value={screenQ.data.params?.n_overlap_months} />
                <Kpi label="Book constituents" value={screenQ.data.reference?.n_constituents ?? '—'} />
                <Kpi label="Book window" value={screenQ.data.reference?.from_date ? `${screenQ.data.reference.from_date} → ${screenQ.data.reference.to_date}` : '—'} />
              </div>

              {screenQ.data.reference?.live_only && (
                <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Book built from <b>live track records only</b> — {screenQ.data.reference.backtest_rows_excluded} backtested
                  monthly observations were excluded. {screenQ.data.reference.caveat}
                </div>
              )}

              <Panel title="Ranked by information ratio vs. the book">
                <div className="mb-2 text-xs text-gray-500">
                  Negative correlation with positive alpha is what diversifies the book — the top of this list.
                </div>
                <div className="max-h-[28rem] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>{SCREEN_COLS.filter(c => screenQ.data.columns.includes(c) || c === 'ticker').map(c => (
                        <th key={c} className={th}>{c.replace(' (m)', '')}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {screenQ.data.rows.map((r: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50">
                          {SCREEN_COLS.filter(c => screenQ.data.columns.includes(c) || c === 'ticker').map(c => (
                            <td key={c} className={td}>
                              {typeof r[c] === 'number'
                                ? (c.includes('alpha') ? `${(r[c] * 100).toFixed(2)}%` : num(r[c], 3))
                                : (r[c] == null ? '—' : String(r[c]).slice(0, 28))}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>

              <Panel title="Correlation to the book vs. annualised alpha">
                <ResponsiveContainer width="100%" height={320}>
                  <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" dataKey="corr" name="corr vs book" tick={{ fontSize: 11 }}
                      label={{ value: 'correlation vs book', position: 'insideBottom', offset: -15, fontSize: 11 }} />
                    <YAxis type="number" dataKey="alpha" name="alpha" tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`} />
                    <ZAxis range={[40, 40]} />
                    <Tooltip formatter={(v: any, n: string) => n === 'alpha' ? `${(v * 100).toFixed(2)}%` : num(v, 3)}
                      labelFormatter={() => ''} content={undefined} />
                    <Scatter
                      data={screenQ.data.rows.map((r: any) => ({
                        corr: r['corr_vs_portfolio (m)'], alpha: r['alpha_ann (m)'], ticker: r.ticker,
                      })).filter((d: any) => d.corr != null && d.alpha != null)}>
                      {screenQ.data.rows.map((r: any, i: number) => (
                        <Cell key={i} fill={(r['corr_vs_portfolio (m)'] ?? 0) < 0 && (r['alpha_ann (m)'] ?? 0) > 0 ? '#059669' : '#94a3b8'} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
                <div className="mt-1 text-xs text-gray-500">Green: negatively correlated to the book and positive alpha.</div>
              </Panel>
            </>
          )}
        </div>
      )}

      {tab === 'Candidates' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Shrinkage k <input className={`${inp} ml-1 w-20`} type="number" step="0.5" value={candK} onChange={e => setCandK(Number(e.target.value))} /></label>
            <label className="text-sm">Max tickers <input className={`${inp} ml-1 w-24`} type="number" value={candLimit} onChange={e => setCandLimit(Number(e.target.value))} /></label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={() => setCandRun(n => n + 1)}><Play size={15} />Build candidate pool</button>
            <button className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={downloadReport}><Layers size={15} />Excel report</button>
          </div>
          {candQ.isFetching && <Spinner label="Screening, de-duplicating and scoring candidates…" />}
          {candQ.data && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Universe" value={candQ.data.n_universe} />
                <Kpi label="Pool (after filters)" value={candQ.data.n_pool} />
                <Kpi label="Candidates (deduped)" value={candQ.data.n_candidates} />
                <Kpi label="Recommended k" value={candQ.data.recommended_shrinkage_k?.recommended_k != null ? Number(candQ.data.recommended_shrinkage_k.recommended_k).toFixed(2) : '—'} />
              </div>
              <Panel title="Scored candidates">
                <p className="mb-2 text-xs text-gray-500">Composite score blends performance, track-record length and diversification, shrunk within each {candQ.data.bucket_field} bucket.</p>
                <div className="max-h-[28rem] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{['Strategy', 'Ticker', 'Risk premia', 'Asset class', 'Sharpe', 'Composite', 'Diversification', 'Rank perf'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {(candQ.data.candidates || []).map((c: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className={`${td} whitespace-normal font-medium`} title={c.strategy}>{c.strategy || <span className="text-gray-400">—</span>}</td>
                          <td className={tickerTd}>{c.ticker}</td>
                          <td className={td}>{c.risk_premia}</td><td className={td}>{c.asset_class}</td>
                          <td className={td}>{num(c.sharpe_ann)}</td>
                          <td className={`${td} font-medium ${(c.composite_score ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{num(c.composite_score)}</td>
                          <td className={td}>{num(c.diversification_score)}</td>
                          <td className={td}>{num(c.rank_perf)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
          {!candQ.data && !candQ.isFetching && <Empty text="Build the candidate pool to see scored strategies." />}
        </div>
      )}

      {/* ------------------------------------------------ Portfolio */}
      {tab === 'Portfolio' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Max constituents <input className={`${inp} ml-1 w-20`} type="number" value={maxConst} onChange={e => setMaxConst(Number(e.target.value))} /></label>
            <label className="text-sm">Min corr threshold <input className={`${inp} ml-1 w-20`} type="number" step="0.05" value={minCorr} onChange={e => setMinCorr(Number(e.target.value))} /></label>
            <label className="text-sm">Shrinkage k <input className={`${inp} ml-1 w-20`} type="number" step="0.5" value={candK} onChange={e => setCandK(Number(e.target.value))} /></label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={() => setSimRun(n => n + 1)}><Play size={15} />Build portfolio</button>
          </div>
          {simQ.isFetching && <Spinner label="Selecting constituents and computing weights…" />}
          {simQ.data?.available && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Candidates" value={simQ.data.n_candidates} />
                <Kpi label="Selected" value={simQ.data.n_selected} />
                <Kpi label="IS Sharpe" value={num(simQ.data.performance?.in_sample?.sharpe)} />
                <Kpi label="OOS Sharpe" value={num(simQ.data.performance?.out_of_sample?.sharpe)} />
              </div>
              {simQ.data.performance?.curve?.length > 0 && (
                <Panel title="Equity curve (selected book)">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={simQ.data.performance.curve} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={50} />
                      <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                      <Tooltip formatter={(v: number) => v.toFixed(3)} />
                      <Line type="monotone" dataKey="value" stroke="#2563eb" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              )}
              <Panel title="Performance by period">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead><tr>{['Period', 'Ann. return', 'Ann. vol', 'Sharpe', 'Max DD', 'Days'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {['full', 'in_sample', 'out_of_sample'].map(leg => {
                      const s = simQ.data.performance?.[leg]
                      if (!s) return null
                      return (
                        <tr key={leg}>
                          <td className={`${td} font-medium`}>{leg.replace('_', '-')}</td>
                          <td className={td}>{(s.ann_return * 100).toFixed(2)}%</td>
                          <td className={td}>{(s.ann_vol * 100).toFixed(2)}%</td>
                          <td className={td}>{num(s.sharpe)}</td>
                          <td className={`${td} text-red-500`}>{(s.max_drawdown * 100).toFixed(2)}%</td>
                          <td className={td}>{s.n_days}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </Panel>
              <Panel title="Selected constituents & weights">
                <div className="max-h-96 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{['Strategy', 'Ticker', 'Weight', 'Provider', 'Risk premia', 'Asset class'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {(simQ.data.performance?.weights || []).map((w: any) => {
                        const meta = (simQ.data.selected || []).find((s: any) => s.ticker === w.ticker) || {}
                        return (
                          <tr key={w.ticker} className="hover:bg-gray-50">
                            <td className={`${td} whitespace-normal font-medium`} title={meta.strategy}>{meta.strategy || <span className="text-gray-400">—</span>}</td>
                            <td className={tickerTd}>{w.ticker}</td>
                            <td className={td}>{(w.weight * 100).toFixed(1)}%</td>
                            <td className={td}>{meta.provider || '—'}</td>
                            <td className={td}>{meta.risk_premia || '—'}</td>
                            <td className={td}>{meta.asset_class || '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
          {simQ.data && !simQ.data.available && <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">{simQ.data.reason}</div>}
          {!simQ.data && !simQ.isFetching && <Empty text="Build a portfolio from the scored candidates." />}
        </div>
      )}

      {/* ------------------------------------------------ Walk-forward */}
      {tab === 'Walk-forward' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Selection window (y) <input className={`${inp} ml-1 w-20`} type="number" value={rollWindow} onChange={e => setRollWindow(Number(e.target.value))} /></label>
            <label className="text-sm">Roll frequency (y) <input className={`${inp} ml-1 w-20`} type="number" value={rollFreq} onChange={e => setRollFreq(Number(e.target.value))} /></label>
            <label className="text-sm">Max constituents <input className={`${inp} ml-1 w-20`} type="number" value={maxConst} onChange={e => setMaxConst(Number(e.target.value))} /></label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={() => setRollRun(n => n + 1)}><Play size={15} />Run walk-forward</button>
            <span className="text-xs text-gray-500">re-screens the universe at every vintage — takes ~30-60s</span>
          </div>
          {rollQ.isFetching && <Spinner label="Walking forward vintage by vintage…" />}
          {rollQ.data?.available && (() => {
            const res = rollQ.data.result || {}
            const wf = res.walk_forward || {}
            return (
              <>
                <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  Each vintage re-derives the universe from a trailing window only, so no period is selected using its own future.
                  {rollQ.data.reference?.note ? ` ${rollQ.data.reference.note}.` : ''}
                </div>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                  <Kpi label="Vintages" value={res.n_vintages} />
                  <Kpi label="Ann. return" value={wf.ann_return != null ? `${(wf.ann_return * 100).toFixed(2)}%` : '—'} />
                  <Kpi label="Ann. vol" value={wf.ann_vol != null ? `${(wf.ann_vol * 100).toFixed(2)}%` : '—'} />
                  <Kpi label="Sharpe" value={num(wf.sharpe)} />
                  <Kpi label="Max DD" value={wf.max_drawdown != null ? `${(wf.max_drawdown * 100).toFixed(2)}%` : '—'} />
                </div>
                {res.equity_curve?.length > 0 && (
                  <Panel title={`Walk-forward equity curve (${wf.start} → ${wf.end})`}>
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={res.equity_curve} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={50} />
                        <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                        <Tooltip formatter={(v: number) => v.toFixed(4)} />
                        <Line type="monotone" dataKey="value" stroke="#059669" dot={false} strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                    <p className="mt-2 text-xs text-gray-500">Vintages: {(res.vintage_dates || []).join(' · ')}</p>
                  </Panel>
                )}
                {res.selected_candidates && (
                  <Panel title={`Final book (${res.selected_candidates.rows} constituents)`}>
                    <div className="max-h-80 overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead><tr>{['Strategy', 'Ticker', 'Provider', 'Risk premia', 'Asset class'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                        <tbody className="divide-y divide-gray-100">
                          {(res.selected_candidates.rows_sample || []).map((r: any, i: number) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className={`${td} whitespace-normal font-medium`} title={r.strategy}>{r.strategy || <span className="text-gray-400">—</span>}</td>
                              <td className={tickerTd}>{r.ticker}</td>
                              <td className={td}>{r.provider}</td><td className={td}>{r.risk_premia}</td><td className={td}>{r.asset_class}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                )}
              </>
            )
          })()}
          {!rollQ.data && !rollQ.isFetching && <Empty text="Run the walk-forward simulation to measure the strategy without survivorship bias." />}
        </div>
      )}

      {/* ------------------------------------------------ Tail risk */}
      {tab === 'Tail risk' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Legs to stress <input className={`${inp} ml-1 w-16`} type="number" value={nLegs} onChange={e => setNLegs(Number(e.target.value))} /></label>
            <label className="text-sm">Max constituents <input className={`${inp} ml-1 w-20`} type="number" value={maxConst} onChange={e => setMaxConst(Number(e.target.value))} /></label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={() => setTrRun(n => n + 1)}><Activity size={15} />Run tail-risk stress</button>
          </div>
          {trQ.isFetching && <Spinner label="Finding peers and replaying drawdown episodes..." />}
          {trQ.data && !trQ.data.available && <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">{trQ.data.reason}</div>}
          {trQ.data?.available && (
            <>
              <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
                Each short-vol leg is swapped for correlated peers across the book's worst drawdown episodes — "how bad could this have been with a different name from the same family?"
              </div>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Constituents" value={trQ.data.n_constituents} />
                <Kpi label="Episodes" value={trQ.data.n_episodes_total} />
                <Kpi label="Episode threshold" value={trQ.data.episode_threshold != null ? `${(trQ.data.episode_threshold * 100).toFixed(2)}%` : '-'} />
                <Kpi label="Baseline DD/vol" value={num(trQ.data.baseline?.dd_to_vol)} />
              </div>
              {Object.entries(trQ.data.reports || {}).map(([leg, rep]: any) => (
                <Panel key={leg} title={`Leg ${leg} - skew ${num(rep.skew)}, ${rep.n_peers} peer(s), ${rep.n_episodes} episodes`}>
                  {rep.scenario_summary?.worst_of && (
                    <div className="mb-3 flex flex-wrap gap-4 text-sm">
                      <span>Worst peer: <b>{rep.scenario_summary.worst_of.peer || '-'}</b></span>
                      <span>DD/vol: <b>{num(rep.scenario_summary.worst_of.dd_to_vol)}</b></span>
                      <span>vs baseline: <b className={(rep.scenario_summary.worst_of.multiple ?? 1) > 1 ? 'text-red-500' : 'text-emerald-600'}>{num(rep.scenario_summary.worst_of.multiple)}x</b></span>
                    </div>
                  )}
                  {rep.episodes?.length > 0 && (
                    <div className="max-h-64 overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead><tr>{Object.keys(rep.episodes[0]).slice(0, 6).map((h: string) => <th key={h} className={th}>{h}</th>)}</tr></thead>
                        <tbody className="divide-y divide-gray-100">
                          {rep.episodes.map((e: any, i: number) => (
                            <tr key={i} className="hover:bg-gray-50">
                              {Object.keys(rep.episodes[0]).slice(0, 6).map((k: string) => (
                                <td key={k} className={td}>{typeof e[k] === 'number' ? num(e[k], 4) : String(e[k]).slice(0, 22)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              ))}
            </>
          )}
          {!trQ.data && !trQ.isFetching && <Empty text="Run the peer-substitution stress on the constructed book." />}
        </div>
      )}

      {/* ------------------------------------------------ Regime Lab */}
      {tab === 'Regime Lab' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Trailing window (days) <input className={`${inp} ml-1 w-20`} type="number" value={bwPeriod} onChange={e => setBwPeriod(Number(e.target.value))} /></label>
            <label className="text-sm">Bins <input className={`${inp} ml-1 w-16`} type="number" value={nBins} onChange={e => setNBins(Number(e.target.value))} /></label>
            <label className="text-sm">Group by
              <select className={`${inp} ml-1`} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                <option value="">(none)</option>
                <option value="asset_class">asset class</option>
                <option value="risk_premia">risk premia</option>
              </select>
            </label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={() => setRgRun(n => n + 1)}><Play size={15} />Run regime study</button>
          </div>
          {rgQ.isFetching && <Spinner label="Building the panel and binning trailing returns..." />}
          {rgQ.data && !rgQ.data.available && <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">{rgQ.data.reason}</div>}
          {rgQ.data?.available && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Strategies" value={rgQ.data.n_tickers} />
                <Kpi label="Observations" value={Number(rgQ.data.n_observations).toLocaleString()} />
                <Kpi label="Trailing window" value={`${rgQ.data.params?.bw_period}d`} />
                <Kpi label="Bins" value={rgQ.data.params?.n_bins} />
              </div>
              {rgQ.data.interpretation && (
                <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">{rgQ.data.interpretation}</div>
              )}
              {!rgQ.data.params?.group_by && (
                <Panel title="Forward return by trailing-return decile">
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={(rgQ.data.rows || []).map((r: any) => ({
                      bin: `${r.bin}`,
                      trailing: r.bw_ret_mean != null ? r.bw_ret_mean * 100 : null,
                      fw1m: r.fw_ret_1m_mean != null ? r.fw_ret_1m_mean * 100 : null,
                      fw3m: r.fw_ret_3m_mean != null ? r.fw_ret_3m_mean * 100 : null,
                    }))} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="bin" tick={{ fontSize: 11 }} label={{ value: 'trailing-return decile (0 = worst)', position: 'insideBottom', offset: -4, fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} unit="%" />
                      <Tooltip formatter={(v: number) => `${Number(v).toFixed(3)}%`} />
                      <Bar dataKey="fw1m" name="Forward 1m" fill="#2563eb" />
                      <Bar dataKey="fw3m" name="Forward 3m" fill="#93c5fd" />
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              )}
              <Panel title="Conditional statistics by bin">
                <div className="max-h-96 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{[rgQ.data.params?.group_by, 'Bin', 'Trailing mean', 'Fwd 1m', 'Fwd 3m', 'Fwd 6m', 'Hit rate 1m', 'Obs'].filter(Boolean).map((h: any) => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {(rgQ.data.rows || []).map((r: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50">
                          {rgQ.data.params?.group_by && <td className={`${td} font-medium`}>{r[rgQ.data.params.group_by]}</td>}
                          <td className={td}>{r.bin}</td>
                          <td className={td}>{r.bw_ret_mean != null ? `${(r.bw_ret_mean * 100).toFixed(2)}%` : '-'}</td>
                          <td className={`${td} ${(r.fw_ret_1m_mean ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{r.fw_ret_1m_mean != null ? `${(r.fw_ret_1m_mean * 100).toFixed(3)}%` : '-'}</td>
                          <td className={td}>{r.fw_ret_3m_mean != null ? `${(r.fw_ret_3m_mean * 100).toFixed(3)}%` : '-'}</td>
                          <td className={td}>{r.fw_ret_6m_mean != null ? `${(r.fw_ret_6m_mean * 100).toFixed(3)}%` : '-'}</td>
                          <td className={td}>{r.fw_ret_1m_hitrate_pos != null ? `${(r.fw_ret_1m_hitrate_pos * 100).toFixed(1)}%` : '-'}</td>
                          <td className={td}>{Number(r.n_obs).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
          {!rgQ.data && !rgQ.isFetching && <Empty text="Run the regime study to see what follows a given trailing move." />}
        </div>
      )}

      {/* ------------------------------------------------ Event study */}
      {tab === 'Event study' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Event start <input className={`${inp} ml-1`} type="date" value={evStart} onChange={e => setEvStart(e.target.value)} /></label>
            <label className="text-sm">Event end <input className={`${inp} ml-1`} type="date" value={evEnd} onChange={e => setEvEnd(e.target.value)} /></label>
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={() => setEvRun(n => n + 1)}><Play size={15} />Run event study</button>
            <span className="text-xs text-gray-500">ranked by sigma move, so a 4%-vol and a 25%-vol index are comparable</span>
          </div>
          {evQ.isFetching && <Spinner label="Scoring the event window..." />}
          {evQ.data && !evQ.data.available && <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">{evQ.data.reason}</div>}
          {evQ.data?.available && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Scored (live)" value={evQ.data.n_scored} />
                <Kpi label="Excluded (backtest only)" value={evQ.data.n_excluded_backtest_only} />
                <Kpi label="Window" value={`${evQ.data.event?.days}d`} />
                <Kpi label="Ranked on" value={evQ.data.params?.rank_col} />
              </div>
              <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
                Strategies whose live date falls after the event start are scored on backtest data — a simulated reaction to a real event is not evidence, so they are excluded rather than mixed in.
              </div>
              {[['worst', 'Worst reactions'], ['best', 'Best reactions']].map(([key, title]) => (
                <Panel key={key} title={title}>
                  <div className="max-h-80 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead><tr>{['Ticker', 'Strategy', 'Risk premia', 'Return %', 'Sigma move', 'Vol pre %', 'Vol event %', 'Vol ratio', 'Obs'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {(evQ.data[key] || []).map((r: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className={`${td} font-medium`}>{r.ticker}</td>
                            <td className={`${td} whitespace-normal`} title={r.strategy}>{r.strategy || '—'}</td>
                            <td className={td}>{r.risk_premia}</td>
                            <td className={`${td} ${(r['ret_%'] ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{num(r['ret_%'])}</td>
                            <td className={`${td} font-medium ${(r.sigma_move ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{num(r.sigma_move)}</td>
                            <td className={td}>{num(r['vol_pre_%'])}</td>
                            <td className={td}>{num(r['vol_event_%'])}</td>
                            <td className={td}>{num(r.vol_ratio, 1)}x</td>
                            <td className={td}>{r.n_obs}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              ))}
              {evQ.data.by_group?.length > 0 && (
                <Panel title="By risk premia">
                  <div className="max-h-72 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead><tr>{Object.keys(evQ.data.by_group[0]).slice(0, 7).map((h: string) => <th key={h} className={th}>{h}</th>)}</tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {evQ.data.by_group.map((r: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50">
                            {Object.keys(evQ.data.by_group[0]).slice(0, 7).map((k: string) => (
                              <td key={k} className={td}>{typeof r[k] === 'number' ? num(r[k]) : String(r[k]).slice(0, 22)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              )}
            </>
          )}
          {!evQ.data && !evQ.isFetching && <Empty text="Pick an event window (the COVID crash is pre-filled) and run the study." />}
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value, icon }: { label: string; value: any; icon?: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-1 text-xs text-gray-400">{icon}{label}</div>
      <div className="mt-1 text-xl font-semibold text-gray-900">{value ?? '—'}</div>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 text-sm font-medium text-gray-700">{title}</div>
      {children}
    </div>
  )
}

function Breakdown({ data }: { data: { name: string; value: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} layout="vertical" margin={{ left: 30, right: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis type="number" tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
        <Tooltip />
        <Bar dataKey="value" fill="#2563eb" />
      </BarChart>
    </ResponsiveContainer>
  )
}

function DecayScatter({ rows }: { rows: any[] }) {
  const pts = (rows || [])
    .filter(r => r.sharpe_ann_bk != null && r.sharpe_ann_live != null)
    .map(r => ({ x: r.sharpe_ann_bk, y: r.sharpe_ann_live, z: Math.abs(r.sharpe_ann_fh ?? 0.5), ticker: r.ticker }))
  return (
    <ResponsiveContainer width="100%" height={360}>
      <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis type="number" dataKey="x" name="Sharpe (backtest)" tick={{ fontSize: 11 }}
          label={{ value: 'Sharpe — backtest', position: 'insideBottom', offset: -10, fontSize: 11 }} />
        <YAxis type="number" dataKey="y" name="Sharpe (live)" tick={{ fontSize: 11 }}
          label={{ value: 'Sharpe — live', angle: -90, position: 'insideLeft', fontSize: 11 }} />
        <ZAxis type="number" dataKey="z" range={[40, 400]} />
        <Tooltip content={({ active, payload }: any) => {
          if (!active || !payload?.length) return null
          const p = payload[0].payload
          return (
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow">
              <div className="font-semibold">{p.ticker}</div>
              <div>backtest {num(p.x)} → live {num(p.y)}</div>
            </div>
          )
        }} />
        <Scatter data={pts}>
          {pts.map((p, i) => <Cell key={i} fill={p.y >= p.x ? '#34a37e' : '#e06666'} fillOpacity={0.7} />)}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  )
}

function Spinner({ label }: { label?: string }) {
  return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={16} />{label || 'Loading…'}</div>
}

function Badge({ children }: { children: any }) {
  return <span className="rounded-md border border-gray-200 bg-white px-3 py-1 text-gray-600">{children}</span>
}

function Empty({ text }: { text: string }) {
  return <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">{text}</div>
}


/**
 * Group cluster rows into families, biggest first.
 *
 * The endpoint returns one row per strategy with a cluster id, and most clusters have
 * exactly one member — on a 120-strategy run, 95 clusters of which only 10 hold more than
 * one. Listing the rows flat under the heading "duplicate families" therefore buries the
 * ten families that are the entire point among eighty-five strategies that are not
 * duplicates of anything.
 */
function groupClusters(rows: any[]): { id: any; rows: any[] }[] {
  const by = new Map<any, any[]>()
  for (const r of rows) {
    const k = r.cluster_id
    if (!by.has(k)) by.set(k, [])
    by.get(k)!.push(r)
  }
  return [...by.entries()]
    .map(([id, rs]) => ({
      // Within a family, the strongest track record first — it is usually the one to keep.
      id,
      rows: [...rs].sort((a, b) => (b.sharpe_ann ?? -99) - (a.sharpe_ann ?? -99)),
    }))
    .sort((a, b) => b.rows.length - a.rows.length || Number(a.id) - Number(b.id))
}
